import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Home, PlusCircle, Receipt, Wallet, FileText, LogOut, Database, Trash2, MessagesSquare, LineChart, CreditCard, Target, History as HistoryIcon, User2, BookOpen, CalendarDays, Banknote, ShieldCheck, Sparkles, Brain, ChevronDown, ChevronsLeft, ChevronsRight, HelpCircle, TrendingUp, Briefcase, Globe, Users, Sun, Moon } from "lucide-react";
import { cn } from "../../lib/utils";
import { tx } from "../../i18n";
import { CurrencySwitcher, LangSwitcher, CountrySwitcher } from "./Switchers";
import { SpaceSwitcher } from "../SpaceSwitcher";
import { useSpace } from "../../contexts/SpaceContext";
import { useDarkMode } from "../../hooks/useDarkMode";
import { api } from "../../lib/api";
import { InstallAppButton } from "../pwa/InstallAppButton";
import { useCountry } from "../../contexts/CountryContext";
import { LumfiMark } from "../LumfiLogo";
import { billingService } from "../../services/billingService";
import { useEntitlements } from "../../hooks/useEntitlements";

// iter-170ah — Workspace hub features. Used both by ``Workspace.jsx``
// to enumerate sections AND by the sidebar to compute the
// "entitled / total" PRO badge next to the nav entry.
export const WORKSPACE_FEATURES = [
  "smart_alerts",
  "accountant_readonly",
  "advanced_exports",
  "basic_api",
  "audit_log",
  "roles_permissions",
];

function WorkspaceBadge() {
  // ``rail`` mode hides ``rightAdornment`` entirely in the parent
  // NavItem (``{!rail && rightAdornment}``), so we don't need to
  // guard for it here.
  const { has, isLoading } = useEntitlements();
  if (isLoading) return null;
  const entitled = WORKSPACE_FEATURES.filter((k) => has(k)).length;
  const total = WORKSPACE_FEATURES.length;
  // ``all-entitled`` ⇒ neutral green; ``some`` ⇒ violet (PRO-leaning);
  // ``none`` ⇒ amber to signal upgrade potential without being alarmist.
  const tone = entitled === total
    ? "bg-emerald-100 text-emerald-700"
    : entitled === 0
      ? "bg-amber-100 text-amber-700"
      : "bg-sky-100 text-sky-700";
  return (
    <span
      className={cn(
        "ml-auto inline-flex items-center text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full",
        tone,
      )}
      data-testid="nav-workspace-badge"
      title={`${entitled} of ${total} workspace features entitled`}
    >
      {entitled}/{total}
    </span>
  );
}

// Persist collapsible section state under a stable key so the user's
// preference survives page reloads / logouts.
const STORAGE_KEY = "lumfi.sidenav.collapsed.v1";
// Distinct key for the whole-sidebar collapsed state — kept apart from
// per-section flags so legacy localStorage data doesn't toggle the rail.
const RAIL_KEY = "lumfi.sidenav.rail.v1";
function readPersisted() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}
function writePersisted(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* localStorage quota / private mode — soft fail */ }
}

function readRail() {
  try { return localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; }
}
function writeRail(v) {
  try { localStorage.setItem(RAIL_KEY, v ? "1" : "0"); } catch { /* see above */ }
}

const primary = (t, lang) => [
  { to: "/dashboard", label: t("home"), icon: Home, testid: "nav-home" },
  { to: "/add", label: t("nav_add"), icon: Sparkles, testid: "nav-add" },
  { to: "/receivables", label: t("nav_receivables"), icon: Receipt, testid: "nav-receivables" },
  { to: "/payments", label: t("nav_payments"), icon: Wallet, testid: "nav-payments" },
  // iter-170an follow-up — Shared cross-workspace operations.
  // Placed in primary nav so it surfaces to all users; gated by
  // membership rather than entitlement (feature is free for now).
  { to: "/shared", label: tx(lang, "Shared", "Compartidas", "Partilhadas"), icon: Users, testid: "nav-shared" },
  { to: "/history", label: t("nav_history"), icon: HistoryIcon, testid: "nav-history" },
];

const wealth = (lang) => [
  // Cuentas es el "ancla" del patrimonio — saldo real disponible.
  { to: "/accounts", label: tx(lang, "Accounts", "Cuentas", "Contas"), icon: Banknote,    testid: "nav-accounts" },
  { to: "/investments",   label: tx(lang, "Investments",   "Inversiones",        "Investimentos"),       icon: LineChart,   testid: "nav-investments" },
  { to: "/debts",         label: tx(lang, "Debts",         "Deudas",             "Dívidas"),             icon: CreditCard,  testid: "nav-debts" },
  { to: "/goals",         label: tx(lang, "Goals",         "Metas",              "Metas"),               icon: Target,      testid: "nav-goals" },
  // iter-170x — Scenarios (what-if) — currently a "coming soon" page
  // until the calculator engine ships; the link is exposed so users
  // can preview the use-cases + subscribe to the launch notification.
  { to: "/scenarios",     label: tx(lang, "Scenarios",     "Escenarios",         "Cenários"),            icon: TrendingUp,  testid: "nav-scenarios" },
];

// iter-170an follow-up #6 — Shared-ops badge for the Compartidas nav.
// Polls every 30s and is intentionally a small DOT (not a number) in
// the rail mode so the icon-only sidebar stays clean. Full mode
// shows the count.
function SharedBadge({ rail }) {
  const { data } = useQuery({
    queryKey: ["shared-badge-count"],
    queryFn: async () => (await api.get("/shared/badge-count")).data,
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: false,
  });
  const total = data?.total || 0;
  if (total === 0) return null;
  if (rail) {
    return (
      <span
        className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500"
        data-testid="nav-shared-badge-dot"
      />
    );
  }
  return (
    <span
      className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold"
      data-testid="nav-shared-badge"
    >
      {total > 9 ? "9+" : total}
    </span>
  );
}


function NavItem({ to, label, Icon, testid, activeClass = "bg-emerald-50 text-emerald-700", rail = false, rightAdornment = null }) {
  return (
    <NavLink
      to={to}
      data-testid={`${testid}-desktop`}
      title={rail ? label : undefined}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200",
          rail ? "px-2 py-2 justify-center" : "px-3 py-2",
          isActive ? activeClass : "text-slate-600 hover:bg-slate-50"
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span
        className={cn(
          "whitespace-nowrap transition-all duration-200 overflow-hidden",
          rail ? "w-0 opacity-0" : "w-auto opacity-100"
        )}
      >
        {label}
      </span>
      {!rail && rightAdornment}
    </NavLink>
  );
}

/**
 * Collapsible section group. Each section persists its open/closed
 * state under `lumfi.sidenav.collapsed.v1[testid]` in localStorage so
 * the user's preference survives reloads. Defaults to CLOSED — the
 * primary nav (Inicio/Agregar/...) is always visible above; secondary
 * sections require an explicit click to expand.
 */
function CollapsibleSection({ label, items, testid, rail = false }) {
  const location = useLocation();
  const containsActive = items.some((it) => location.pathname.startsWith(it.to));

  // Persisted state wins; otherwise default to closed (matches the
  // shape of the screenshot the user shared — slim sidenav by default).
  const persisted = readPersisted();
  const initial = persisted[testid] !== undefined ? persisted[testid] : false;
  const [open, setOpen] = useState(initial);

  // When the active route is inside this section we always open it,
  // but we don't write that back to localStorage — the user's manual
  // preference stays intact for other routes.
  const effectiveOpen = open || containsActive;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writePersisted({ ...readPersisted(), [testid]: next });
  };

  // In rail mode the section header is hidden and items render
  // unconditionally — there's no room to show "Cuentas y patrimonio"
  // labels anyway, and a hidden chunk of icons defeats the point of
  // collapsing to a rail. Tooltip on each icon handles discoverability.
  if (rail) {
    return (
      <div data-testid={`sidenav-section-${testid}`}>
        <div className="flex flex-col gap-1">
          {items.map((it) => (
            <NavItem
              key={it.to}
              to={it.to}
              label={it.label}
              Icon={it.icon}
              testid={it.testid}
              activeClass={it.activeClass}
              rail
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`sidenav-section-${testid}`}>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={toggle}
        data-testid={`sidenav-toggle-${testid}`}
        className="w-full flex items-center justify-between gap-2 px-3 pt-2 pb-1 group"
      >
        <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider group-hover:text-slate-600 transition-colors">
          {label}
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 text-slate-400 group-hover:text-slate-600 transition-all duration-200",
            !effectiveOpen && "-rotate-90"
          )}
        />
      </button>
      {effectiveOpen && (
        <div className="flex flex-col gap-1">
          {items.map((it) => (
            <NavItem
              key={it.to}
              to={it.to}
              label={it.label}
              Icon={it.icon}
              testid={it.testid}
              activeClass={it.activeClass}
              rightAdornment={it.rightAdornment}
              rail={rail}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function DesktopSidebar({
  t, lang, setLang, user, logout, navigate, loadDemo, onClearDemo,
  currency, setCurrency, fxList, isEs,
}) {
  const primaryList = primary(t, lang);
  const wealthList = wealth(lang);
  const [rail, setRail] = useState(() => readRail());
  // iter-213.9 — dark mode toggle, moved here from Dashboard so the
  // button is reachable from every page (the user expectation is a
  // "theme switch" not "dashboard-only switch"). The Dashboard's own
  // toggle was removed in this iteration.
  const [isDarkSidebar, toggleDarkSidebar] = useDarkMode();

  // Surface "Bono" indicator across the app when the user is on a
  // grant-only plan. Cheap query (5min cache) — the same one MyPlan
  // already hits, so it ends up sharing the cache.
  const { data: billingMe } = useQuery({
    queryKey: ["billing-me"],
    queryFn: () => billingService.me(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const viaGrant = !!billingMe?.via_grant;

  // iter-169z6 — Surface the most-pressing low-balance feature inline
  // in the sidebar. Mirrors the dashboard `FeatureUsageAlert` chip but
  // collapsed to a small red pill next to "Planes y packs" so the user
  // always sees the upgrade nudge without dismissing modals.
  const lowFeature = useMemo(() => {
    const items = billingMe?.usage?.feature_usage || [];
    let best = null;
    for (const it of items) {
      const allot = Number(it.allotment) || 0;
      if (allot <= 0) continue;
      const remaining = Math.max(0, Number(it.remaining ?? (allot - (it.used || 0))));
      if (remaining / allot >= 0.2) continue;
      if (!best || remaining < best.remaining) best = { ...it, remaining };
    }
    return best;
  }, [billingMe]);
  const lowFeatureLabel = useMemo(() => {
    if (!lowFeature) return "";
    const map = {
      image_extractions: { es: "recibos IA", en: "AI receipts", pt: "recibos IA" },
      advisor_chat:      { es: "consultas IA", en: "AI advisor", pt: "consultas IA" },
    };
    const m = map[lowFeature.feature_key];
    return m ? (m[lang] || m.es) : lowFeature.feature_key;
  }, [lowFeature, lang]);

  const toggleRail = () => {
    setRail((prev) => {
      const next = !prev;
      writeRail(next);
      return next;
    });
  };

  const { isMultiWorkspaceUser } = useSpace();

  // iter-185 — Referrals menu is only visible when the campaign has at
  // least one active rule. Cheap query, 5min cache. When no rules, we
  // skip adding the nav item entirely so the link doesn't dangle.
  const { data: referralRules } = useQuery({
    queryKey: ["referrals-active-rules"],
    queryFn: async () => (await api.get("/referrals/active-rules")).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const referralsActive = !!referralRules?.any_active;

  const productivityItems = useMemo(() => {
    const items = [
      // Análisis: Reporte mensual, calendario, sugerencias IA. Movido aquí
      // desde primario porque son features de "consultar/decidir" más que
      // de registro diario.
      { to: "/report",          testid: "nav-report",          icon: FileText,     label: tx(lang, "Report", "Reporte", "Relatório"),                              activeClass: "bg-sky-50 text-sky-700" },
      { to: "/calendar",        testid: "nav-calendar",        icon: CalendarDays, label: tx(lang, "Calendar", "Calendario", "Calendário"),                       activeClass: "bg-sky-50 text-sky-700" },
      { to: "/recommendations", testid: "nav-recommendations", icon: Brain,        label: tx(lang, "Recommendations", "Recomendaciones", "Recomendações"),       activeClass: "bg-sky-50 text-sky-700" },
      // iter-170ag — Workspace hub for plan-gated team/governance/API
      // features (smart_alerts, accountant_readonly, advanced_exports,
      // basic_api, audit_log, roles_permissions). Always visible so the
      // upsell is reachable; sections inside gate themselves via
      // LockedSection.
      { to: "/workspace",       testid: "nav-workspace",       icon: Briefcase,    label: tx(lang, "Workspace", "Espacio de trabajo", "Espaço de trabalho"),    activeClass: "bg-sky-50 text-sky-700", rightAdornment: <WorkspaceBadge /> },
    ];
    // iter-170am-part5 — "Vista global" sólo aparece cuando el usuario
    // tiene la opción de múltiples workspaces (más de 1 OR con
    // entitlement para crear más). De lo contrario el link agrega ruido.
    if (isMultiWorkspaceUser) {
      items.push(
        { to: "/dashboard/global", testid: "nav-global-view", icon: Globe, label: tx(lang, "Global view", "Vista global", "Vista global"), activeClass: "bg-sky-50 text-sky-700" },
      );
    }
    return items;
  }, [lang, isMultiWorkspaceUser]);

  const connectivityItems = useMemo(() => [
    { to: "/integrations", testid: "nav-integrations", icon: MessagesSquare, label: tx(lang, "Integrations", "Integraciones", "Integrações"), activeClass: "bg-sky-50 text-sky-700" },
    { to: "/bots-guide",   testid: "nav-bots-guide",   icon: BookOpen,       label: tx(lang, "Bots guide", "Guía de bots", "Guia de bots"),   activeClass: "bg-sky-50 text-sky-700" },
  ], [lang]);

  const accountItems = useMemo(() => {
    const myPlanLabel = tx(lang, "My plan", "Mi plan", "Meu plano");
    const base = [
      {
        to: "/my-plan",
        testid: "nav-my-plan",
        icon: CreditCard,
        label: myPlanLabel,
        // 🎁 BONO badge: surfaced globally so a comped user is reminded
        // (in every screen) that they're on a courtesy plan and any
        // expiration applies. Suppressed when the user has a paid
        // composition (via_grant is then false).
        rightAdornment: viaGrant ? (
          <span
            data-testid="nav-bono-badge"
            className="ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 text-[9px] font-bold uppercase tracking-wider"
            title={tx(lang,
              "Courtesy plan granted by the Lumfi team.",
              "Plan de cortesía otorgado por el equipo Lumfi.",
              "Plano cortesia concedido pela equipe Lumfi.")}
          >
            🎁 {tx(lang, "Bonus", "Bono", "Bônus")}
          </span>
        ) : null,
      },
      { to: "/profile", testid: "nav-profile", icon: User2,     label: tx(lang, "My profile", "Mi perfil", "Meu perfil") },
      ...(referralsActive ? [{
        to: "/me/referrals",
        testid: "nav-referrals",
        icon: Users,
        label: tx(lang, "Referrals", "Referidos", "Indicações"),
      }] : []),
      { to: "/help", testid: "nav-help", icon: HelpCircle, label: tx(lang, "Help", "Ayuda", "Ajuda") },
      { to: "/pricing", testid: "nav-pricing", icon: Sparkles,  label: tx(lang, "Plans & packs", "Planes y packs", "Planos e packs"),
        rightAdornment: lowFeature ? (
          <span
            data-testid="nav-pricing-low-feature-badge"
            className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-300 text-[10px] font-bold"
            title={
              lang === "en"
                ? `${lowFeature.remaining} ${lowFeatureLabel} left this month — buy a pack`
                : `Te quedan ${lowFeature.remaining} ${lowFeatureLabel} — compra un pack`
            }
          >
            {lowFeature.remaining} {lowFeatureLabel}
          </span>
        ) : null,
      },
    ];
    if (user?.is_admin) {
      base.push({ to: "/admin", testid: "nav-admin", icon: ShieldCheck, label: tx(lang, "Admin panel", "Panel admin", "Painel admin"), activeClass: "bg-sky-50 text-sky-700" });
    }
    return base;
  }, [lang, user?.is_admin, viaGrant, lowFeature, lowFeatureLabel, referralsActive]);

  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col border-r border-cream-300 bg-white sticky top-0 h-screen",
        "transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
        rail ? "w-[72px]" : "w-64"
      )}
      data-rail={rail ? "true" : "false"}
      data-testid="desktop-sidebar"
    >
      {/* HEADER — fixed at top */}
      <div
        className={cn(
          "pt-5 pb-3 shrink-0 flex items-center gap-2.5 transition-all duration-300",
          rail ? "px-2 justify-center" : "px-4"
        )}
      >
        <LumfiMark size={36} />
        <div
          className={cn(
            "flex flex-col leading-none overflow-hidden transition-all duration-300",
            rail ? "w-0 opacity-0" : "w-auto opacity-100"
          )}
        >
          <span className="font-serif-display text-2xl font-medium text-slate-900 tracking-tight whitespace-nowrap">Lumfi</span>
          <span className="text-[9px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mt-0.5 whitespace-nowrap">Cash Pilot</span>
        </div>
      </div>

      {/* RAIL TOGGLE — sits just under the header. Pill-shaped button
          with subtle bg so it's discoverable but doesn't fight nav
          items for attention. Tooltip clarifies intent in rail mode
          where the label is hidden. */}
      <div className={cn("shrink-0 mb-1", rail ? "px-2" : "px-4")}>
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={toggleRail}
          data-testid="sidenav-rail-toggle"
          title={rail
            ? tx(lang, "Expand", "Expandir", "Expandir")
            : tx(lang, "Collapse", "Contraer", "Contrair")}
          aria-label={rail
            ? tx(lang, "Expand sidebar", "Expandir barra lateral", "Expandir barra lateral")
            : tx(lang, "Collapse sidebar", "Contraer barra lateral", "Contrair barra lateral")}
          className={cn(
            "group w-full flex items-center rounded-full bg-slate-100 hover:bg-slate-200/80",
            "text-slate-600 hover:text-slate-900 transition-all duration-200",
            rail
              ? "justify-center h-9 px-2"
              : "justify-between h-9 px-3 pl-3 pr-3.5"
          )}
        >
          {rail ? (
            <ChevronsRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          ) : (
            <>
              <span className="text-[11px] uppercase tracking-wider font-bold">
                {tx(lang, "Collapse", "Contraer", "Contrair")}
              </span>
              <ChevronsLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            </>
          )}
        </button>
      </div>

      {/* SPACE SWITCHER — sits below the rail toggle, above the nav.
          Surfaces the active workspace and lets the user jump to any
          of their other spaces. Picking a different space triggers a
          full SPA reload so cached queries are rebuilt under the new
          scope. */}
      <div className={cn("shrink-0 mb-2", rail ? "px-2" : "px-4")}>
        <SpaceSwitcher rail={rail} />
      </div>

      {/* SCROLLABLE NAV — primary nav + collapsible sections all live
          inside this flexbox child so the footer below stays pinned
          to the bottom of the viewport, always reachable. */}
      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto pb-2 transition-all duration-300",
          rail ? "px-2" : "px-4"
        )}
        data-testid="sidenav-scroll-area"
      >
        <nav className="flex flex-col gap-1 mb-1">
          {primaryList.map((it) => (
            <NavItem
              key={it.to} to={it.to} label={it.label} Icon={it.icon} testid={it.testid} rail={rail}
              rightAdornment={
                /* iter-170an follow-up #6 — Shared ops pending badge.
                   Renders a red dot/pill next to the Compartidas link
                   when the user has any pending FX-approval OR
                   pending destination-confirm count. */
                it.to === "/shared" ? <SharedBadge rail={rail} /> : null
              }
            />
          ))}
        </nav>

        <CollapsibleSection
          label={tx(lang, "Accounts & wealth", "Cuentas y patrimonio", "Contas e patrimônio")}
          items={wealthList.map((w) => ({ ...w, activeClass: "bg-sky-50 text-sky-700" }))}
          testid="wealth"
          rail={rail}
        />
        <CollapsibleSection
          label={tx(lang, "Analysis", "Análisis", "Análise")}
          items={productivityItems}
          testid="productivity"
          rail={rail}
        />
        <CollapsibleSection
          label={tx(lang, "Integrations", "Integraciones", "Integrações")}
          items={connectivityItems}
          testid="connectivity"
          rail={rail}
        />
        <CollapsibleSection
          label={tx(lang, "Account", "Cuenta", "Conta")}
          items={accountItems}
          testid="account"
          rail={rail}
        />
      </div>

      {/* FOOTER — sticky-pinned at the bottom. In rail mode we hide
          most chrome (demo buttons, switchers) and only keep the
          logout icon so the rail stays visually clean. */}
      <div
        className={cn(
          "shrink-0 border-t border-slate-200 py-3 bg-white transition-all duration-300",
          rail ? "px-2" : "px-4"
        )}
        data-testid="sidenav-footer"
      >
        {!rail && (
          <>
            <div className="flex gap-1">
              <button /* design-system: ALLOW legacy-todo */
                data-testid="load-demo-btn"
                onClick={loadDemo}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] text-slate-500 hover:bg-slate-50"
                title={t("load_demo")}
              >
                <Database className="h-3.5 w-3.5" />
                {tx(lang, "Demo", "Demo", "Demo")}
              </button>
              <button /* design-system: ALLOW legacy-todo */
                data-testid="clear-demo-btn"
                onClick={onClearDemo}
                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] text-red-600 hover:bg-red-50"
                title={t("clear_demo")}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {tx(lang, "Clear", "Limpiar", "Limpar")}
              </button>
            </div>

            <div className="mt-2 text-xs text-slate-500 truncate">{user?.email}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <LangSwitcher testid="lang-switcher-desktop" lang={lang} setLang={setLang} t={t} />
              <CurrencySwitcher testid="currency-switcher-desktop" currency={currency} fxList={fxList} t={t} isEs={isEs} onSelect={setCurrency} />
              <DesktopCountrySwitcher isEs={isEs} />
            </div>
            <div className="mt-2">
              <InstallAppButton
                size="sm"
                variant="outline"
                className="w-full h-7 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              />
            </div>
          </>
        )}
        <button /* design-system: ALLOW legacy-todo */
          data-testid="dashboard-dark-toggle"
          onClick={toggleDarkSidebar}
          className={cn(
            "mt-2 flex items-center text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors",
            rail ? "justify-center w-full p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" : "gap-2"
          )}
          title={rail ? (isDarkSidebar
            ? tx(lang, "Light mode", "Modo claro", "Modo claro")
            : tx(lang, "Dark mode", "Modo oscuro", "Modo escuro")) : undefined}
          aria-label={isDarkSidebar
            ? tx(lang, "Switch to light mode", "Cambiar a modo claro", "Mudar para modo claro")
            : tx(lang, "Switch to dark mode", "Cambiar a modo oscuro", "Mudar para modo escuro")}
        >
          {isDarkSidebar ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {!rail && (isDarkSidebar
            ? tx(lang, "Light mode", "Modo claro", "Modo claro")
            : tx(lang, "Dark mode", "Modo oscuro", "Modo escuro"))}
        </button>
        <button /* design-system: ALLOW legacy-todo */
          data-testid="logout-btn-desktop"
          onClick={async () => { await logout(); navigate("/"); }}
          className={cn(
            "mt-2 flex items-center text-sm text-slate-600 hover:text-red-600 transition-colors",
            rail ? "justify-center w-full p-2 rounded-lg hover:bg-rose-50" : "gap-2"
          )}
          title={rail ? t("logout") : undefined}
        >
          <LogOut className="h-4 w-4" />
          {!rail && t("logout")}
        </button>
      </div>
    </aside>
  );
}

/* Wraps the CountrySwitcher with the country context so the parent
 * doesn't have to thread the props through. The desktop sidebar
 * footer was the only consumer historically; keeping the helper
 * inside this file avoids polluting the global `Switchers` import. */
function DesktopCountrySwitcher({ isEs }) {
  const { country, setCountry, countries } = useCountry();
  return (
    <CountrySwitcher
      testid="country-switcher-desktop"
      country={country}
      countries={countries}
      isEs={isEs}
      onSelect={setCountry}
    />
  );
}

